import { computed }                       from 'mobx';
import { Model }                          from '@mathquis/modelx';
import { WebStorageConnector }            from '@mathquis/modelx-webstorage-connector';
import * as Sentry                        from '@sentry/react';
import LoginCheckModel                    from 'models/LoginCheckModel';
import RefreshTokenModel                  from 'models/RefreshTokenModel';
import { ROLE_COORDINATOR_SPS }           from 'constants/RoleReferences';
import { ROLE_EXHIBITION_CENTER_SERVICE } from 'constants/RoleReferences';
import { ROLE_ORGANIZER }                 from 'constants/RoleReferences';
import { ROLE_SECURITY_OFFICER }          from 'constants/RoleReferences';
import { ROLE_STANDIST }                  from 'constants/RoleReferences';
import { ROLE_SUPPORT_ASTECH }            from 'constants/RoleReferences';
import { jwtDecode }                      from 'jwt-decode';

interface TokenPayload {
  exp: number;
  iat: number;
  username: string;
  user_id: number;
  roles: string[];
  user_informations: {
    email: string;
    firstName?: string;
    lastName?: string;
    language_preference?: string;
  };
}

const emptyToken: TokenPayload = {
  exp: 0,
  iat: 0,
  username: '',
  user_id: 0,
  roles: [],
  user_informations: {
    email: '',
  },
};

const localStorageConnector = new WebStorageConnector({
  storage: localStorage,
});

class Session extends Model {
  static get connector() {
    return localStorageConnector;
  }

  static get path() {
    return '/session';
  }

  static get defaults(): {
    exp: string;
    refreshToken: string;
    showId: number | null;
    token: string;
  } {
    return {
      exp: '',
      refreshToken: '',
      showId: null,
      token: '',
    };
  }

  async getToken() {
    return this.get('token');
  }

  initialize() {
    return this.fetch().then(null, () => {
      // just catch error
    });
  }

  @computed
  get isLoggedIn() {
    return !!this.get('token');
  }

  get isAdminOrSupport() {
    return this.isAdmin || this.isSupportAstech;
  }

  get isGrantedRoleAdmin() {
    return this.isAdmin;
  }

  get isAdmin() {
    if (!this.get('token')) {
      return false;
    }
    return (this.decodedToken.roles || []).find((role: string) => role.match(/ADMIN/g));
  }

  @computed
  get isSupportAstech() {
    return this.isRole(ROLE_SUPPORT_ASTECH);
  }

  @computed
  get isCoordinatorSps() {
    return this.isRole(ROLE_COORDINATOR_SPS);
  }

  @computed
  get isSecurityOfficer() {
    return this.isRole(ROLE_SECURITY_OFFICER);
  }

  @computed
  get isExhibitionCenterService() {
    return this.isRole(ROLE_EXHIBITION_CENTER_SERVICE);
  }

  @computed
  get isOrganizer() {
    return this.isRole(ROLE_ORGANIZER);
  }

  @computed
  get isStandist() {
    return this.isRole(ROLE_STANDIST);
  }

  isRole(roleReference: string) {
    return !!(this.decodedToken.roles || []).find(role => role === roleReference);
  }

  @computed
  get isExpired() {
    return this.get('exp', 0) < Math.ceil(new Date().valueOf() / 1000);
  }

  async login(_username: string, _password: string) {
    const loginCheckModel = new LoginCheckModel();
    await loginCheckModel.set({ _password, _username }).save();

    await this.onLoggedIn(loginCheckModel.get('token'), loginCheckModel.get('refresh_token'));
  }

  async logout() {
    this.set('token', '');
    this.set('refreshToken', '');
    this.set('exp', '');
    await this.save();

    Sentry.setContext('user', {});
  }

  // Shared by concurrent requests hitting an expired token, so only one
  // /token/refresh call is made at a time.
  private refreshPromise: Promise<void> | null = null;

  refresh() {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }

    return this.refreshPromise;
  }

  private async doRefresh() {
    const refreshTokenModel = new RefreshTokenModel();

    try {
      await refreshTokenModel
        .set({
          refresh_token: this.refreshToken,
        })
        .save();
    } catch {
      // refresh token expired/invalid: fall through to the logout/redirect below
    }

    if (typeof refreshTokenModel.get('token') === 'undefined') {
      await this.logout();
      window.location.href = '/login';

      // Never resolve: pending requests must not be sent with an empty token
      // (they would be aborted by the navigation and reported to Sentry as
      // "Network Error").
      return new Promise<void>(() => {});
    }

    await this.onLoggedIn(refreshTokenModel.get('token'), refreshTokenModel.get('refresh_token'));
  }

  set showId(showId) {
    this.set(`${this.decodedToken.user_id}_showId`, showId);
    this.save();
  }

  get showId() {
    return this.get(`${this.decodedToken.user_id}_showId`);
  }

  get decodedToken(): TokenPayload {
    if (!this.token) {
      return emptyToken;
    }

    return jwtDecode(this.token);
  }

  get token() {
    return this.get('token');
  }

  get refreshToken() {
    return this.get('refreshToken');
  }

  // @deprecated, use decodedToken instead
  @computed
  get user() {
    return this.decodedToken;
  }

  async onLoggedIn(token: string, refreshToken: string) {
    this.set({ refreshToken, token });
    const decodedToken = this.decodedToken;
    this.set('exp', decodedToken.exp);

    await this.save();

    Sentry.setUser({
      id: decodedToken.user_id,
      email: decodedToken.user_informations.email,
      username: decodedToken.username,
      roles: decodedToken.roles,
    });

    // Sentry.setContext('user', {
    //   email: decodedToken.user_informations.email,
    //   id: decodedToken.user_id,
    //   username: decodedToken.username,
    // });
  }
}

export default new Session();
